// Application Messages - Centralized user-facing and error messages
import i18n from '@/i18n/config';

export const MESSAGES = {
  // Auth
  AUTH: {
    get GOOGLE_SIGN_IN_FAILED() {
      return i18n.t('messages.auth.googleSignInFailed');
    },
    get APPLE_SIGN_IN_FAILED() {
      return i18n.t('messages.auth.appleSignInFailed');
    },
    get APPLE_INVALID_CLIENT_ID() {
      return i18n.t('messages.auth.appleInvalidClientId');
    },
    get LOGIN_FAILED() {
      return i18n.t('messages.auth.loginFailed');
    },
    get INVALID_LOGIN_RESPONSE() {
      return i18n.t('messages.auth.invalidLoginResponse');
    },
    get TOO_MANY_ATTEMPTS() {
      return i18n.t('messages.auth.tooManyAttempts');
    },
    get NETWORK_ERROR() {
      return i18n.t('messages.auth.networkError');
    },
    get INVALID_CREDENTIALS() {
      return i18n.t('messages.auth.invalidCredentials');
    },
    get WALLET_INITIALIZED_SUCCESSFULLY() {
      return i18n.t('messages.auth.walletInitializedSuccessfully');
    },
    get WALLET_INITIALIZATION_FAILED() {
      return i18n.t('messages.auth.walletInitializationFailed');
    },
    get WALLET_LINK_FAILED() {
      return i18n.t('messages.auth.walletLinkFailed');
    },
    get OTP_SEND_FAILED() {
      return i18n.t('messages.auth.otpSendFailed');
    },
    get INVALID_OTP() {
      return i18n.t('messages.auth.invalidOtp');
    },
    get EMAIL_UNVERIFIED() {
      return i18n.t('messages.auth.emailUnverified');
    },
    get VERIFICATION_EMAIL_SENT() {
      return i18n.t('messages.auth.verificationEmailSent');
    },
    get RESEND_VERIFICATION_LINK_FAILED() {
      return i18n.t('messages.auth.resendVerificationLinkFailed');
    },
  },

  // API
  API: {
    get TOKEN_REFRESH_FAILED() {
      return i18n.t('messages.api.tokenRefreshFailed');
    },
    get NO_ACCESS_TOKEN() {
      return i18n.t('messages.api.noAccessToken');
    },
    get INVALID_REFRESH_RESPONSE() {
      return i18n.t('messages.api.invalidRefreshResponse');
    },
    get INVALID_API_RESPONSE() {
      return i18n.t('messages.api.invalidApiResponse');
    },
    MARKET_NOT_FOUND: (id: string) => i18n.t('messages.api.marketNotFound', { id }),
    EVENT_NOT_FOUND: (id: string) => i18n.t('messages.api.eventNotFound', { id }),
    get BAD_GATEWAY() {
      return i18n.t('messages.api.badGateway');
    },
  },

  // General
  GENERAL: {
    get UNEXPECTED_ERROR() {
      return i18n.t('messages.general.unexpectedError');
    },
    get SOMETHING_WENT_WRONG() {
      return i18n.t('messages.general.somethingWentWrong');
    },
    get LINK_COPIED() {
      return i18n.t('messages.general.linkCopied');
    },
  },

  // Profile
  PROFILE: {
    get NAME_REQUIRED() {
      return i18n.t('messages.profile.nameRequired');
    },
    get UPDATE_SUCCESS() {
      return i18n.t('messages.profile.updateSuccess');
    },
  },
  // Referral — toast notifications only
  REFERRAL: {
    get CODE_REQUIRED() {
      return i18n.t('messages.referral.codeRequired');
    },
    get APPLY_SUCCESS() {
      return i18n.t('messages.referral.applySuccess');
    },
    get APPLY_ERROR() {
      return i18n.t('messages.referral.applyError');
    },
    get LINK_COPIED() {
      return i18n.t('messages.referral.linkCopied');
    },
    get COPY_FAILED() {
      return i18n.t('messages.referral.copyFailed');
    },
  },
  // Toast Notifications
  TOAST: {
    get ADDRESS_COPIED() {
      return i18n.t('messages.toast.addressCopied');
    },
    get ADDRESS_COPIED_CLIPBOARD() {
      return i18n.t('messages.toast.addressCopiedClipboard');
    },
    get COPIED() {
      return i18n.t('messages.toast.copied');
    },
    get WALLET_NOT_CONNECTED() {
      return i18n.t('messages.toast.walletNotConnected');
    },
    get NO_CONDITION_ID() {
      return i18n.t('messages.toast.noConditionId');
    },
    get INVALID_POSITION_DATA() {
      return i18n.t('messages.toast.invalidPositionData');
    },
    get TRADE_HISTORY_EXPORTED() {
      return i18n.t('messages.toast.tradeHistoryExported');
    },
    get ORDER_PLACED() {
      return i18n.t('messages.toast.orderPlaced');
    },
    get ORDER_CANCELLED() {
      return i18n.t('messages.toast.orderCancelled');
    },
    get PAYOUT_SUCCESSFUL() {
      return i18n.t('messages.toast.payoutSuccessful');
    },
    get ALL_OPEN_ORDERS_CANCELLED() {
      return i18n.t('messages.toast.allOpenOrdersCancelled');
    },
    get TRADING_ACCOUNT_INITIALIZED() {
      return i18n.t('messages.toast.tradingAccountInitialized');
    },
    get TRADING_CLIENT_NOT_READY() {
      return i18n.t('messages.toast.tradingClientNotReady');
    },
    get TRADING_CLIENT_PROXY_NOT_READY() {
      return i18n.t('messages.toast.tradingClientProxyNotReady');
    },
    get TRANSFER_SUCCESSFUL() {
      return i18n.t('messages.toast.transferSuccessful');
    },
    get LINK_COPIED_CLIPBOARD() {
      return i18n.t('messages.toast.linkCopiedClipboard');
    },
    get FAILED_SETUP_TRADING_WALLET() {
      return i18n.t('messages.toast.failedSetupTradingWallet');
    },
    get DEPOSIT_INITIATION_FAILED() {
      return i18n.t('messages.toast.depositInitiationFailed');
    },
    get NETWORK_TIMEOUT_RETRY() {
      return i18n.t('messages.toast.networkTimeoutRetry');
    },
    get ORDER_PENDING_CONFIRMATION() {
      return i18n.t('messages.toast.orderPendingConfirmation');
    },
    get ACTIVE_TRADING_WALLET_UPDATED() {
      return i18n.t('messages.toast.activeTradingWalletUpdated');
    },
    get COULD_NOT_SWITCH_WALLET() {
      return i18n.t('messages.toast.couldNotSwitchWallet');
    },
    get IMAGE_INVALID() {
      return i18n.t('messages.toast.imageInvalid');
    },
    get IMAGE_SIZE_EXCEEDED() {
      return i18n.t('messages.toast.imageSizeExceeded');
    },
    get REFERRAL_CONFIG_UPDATED() {
      return i18n.t('messages.toast.referralConfigUpdated');
    },
    get USER_STATUS_UPDATED() {
      return i18n.t('messages.toast.userStatusUpdated');
    },
    get INVITES_SENT() {
      return i18n.t('messages.toast.invitesSent');
    },
    INCOMING_TRANSACTION: (amount: string, tokenName: string) =>
      i18n.t('messages.toast.incomingTransaction', { amount, tokenName }),
    FUNDS_MOVED: (amount: string) => i18n.t('messages.toast.fundsMoved', { amount }),
    get NO_TRADE_HISTORY() {
      return i18n.t('messages.toast.noTradeHistory');
    },
    get MOVING_FUNDS() {
      return i18n.t('messages.toast.movingFunds');
    },
    get ESTABLISHING_WALLET() {
      return i18n.t('messages.toast.establishingWallet');
    },
    get COLLATERAL_UPGRADE_SUBMITTED() {
      return i18n.t('messages.toast.collateralUpgradeSubmitted');
    },
    get CONVERTING_USDC_PUSD() {
      return i18n.t('messages.toast.convertingUsdcPusd');
    },
    get COLLATERAL_UNWRAP_SUBMITTED() {
      return i18n.t('messages.toast.collateralUnwrapSubmitted');
    },
    get SETTING_TOKEN_APPROVALS() {
      return i18n.t('messages.toast.settingTokenApprovals');
    },
    get SUBMITTING_TRANSFER() {
      return i18n.t('messages.toast.submittingTransfer');
    },
    get TRANSACTION_WAITING() {
      return i18n.t('messages.toast.transactionWaiting');
    },
    get AUTO_MIGRATION_PAUSED() {
      return i18n.t('messages.toast.autoMigrationPaused');
    },
    get AUTH_INITIALIZING() {
      return i18n.t('messages.toast.authInitializing');
    },
    get AUTH_INITIALIZING_RETRY() {
      return i18n.t('messages.toast.authInitializingRetry');
    },
    get VERIFICATION_RESEND_HANDLED() {
      return i18n.t('messages.toast.verificationResendHandled');
    },
    get WALLET_NEEDS_POL_GAS() {
      return i18n.t('messages.toast.walletNeedsPolGas');
    },
  },
} as const;
